import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { calculateBrandStats } from "@/lib/brandStats";

// Cache the threshold across the session so every page shows the same limit without refetching.
let _cachedThreshold = null;

// Fetch the admin-configured manager-approval discount threshold (%). Defaults to 60.
export function useApprovalThreshold() {
  const [threshold, setThreshold] = useState(_cachedThreshold ?? 60);
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/quotations/approval-config`, { withCredentials: true })
      .then((r) => {
        const t = Number(r.data?.threshold);
        if (!Number.isNaN(t)) {
          _cachedThreshold = t;
          if (alive) setThreshold(t);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return threshold;
}

// Trigger from an already-computed overall discount + brandStats map (used on Create/Edit pages).
export function computeApprovalTrigger(overallDiscount, brandStatsMap, threshold) {
  const t = Number(threshold ?? 60);
  const brandsExceeding = Object.entries(brandStatsMap || {})
    .filter(([, s]) => Number(s?.discount ?? 0) > t)
    .map(([b]) => b);
  const overallExceeds = Number(overallDiscount || 0) > t;
  return { required: overallExceeds || brandsExceeding.length > 0, overallExceeds, brandsExceeding };
}

// Trigger from a raw quotation/draft object (uses the app-wide brand-wise calc).
export function computeApprovalTriggerFromQuotation(q, threshold) {
  const overall = Number(q?.effective_discount || 0);
  const brandStats = calculateBrandStats(q?.items || [], q?.freebies || []);
  const trig = computeApprovalTrigger(overall, brandStats, threshold);
  return { ...trig, overall, brandStats };
}
